import { useEffect, useState } from "react";
import { Check, TrendingUp, ListChecks } from "lucide-react";
import { api } from "@/lib/api";
import { LOGO_ICON } from "@/components/common";

export const vid = () => {
  let v = localStorage.getItem("sb_vid");
  if (!v) { v = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("sb_vid", v); }
  return v;
};
export const source = () => {
  const p = new URLSearchParams(window.location.search).get("utm_source");
  if (p) return p.toLowerCase();
  try { const h = document.referrer && new URL(document.referrer).hostname; return h && !h.includes(window.location.hostname) ? h.replace(/^www\./, "") : "direct"; } catch { return "direct"; }
};
export const track = (type, extra = {}) => api.post("/public/events", { type, visitor_id: vid(), source: source(), ...extra }).catch(() => {});
export const rs = (n) => `Rs ${Number(n).toLocaleString("en-US")}`;

export function useCountdown(iso) {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!iso) return;
    const tick = () => setLeft(Math.max(0, new Date(iso).getTime() - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [iso]);
  if (left == null || left <= 0) return null;
  const d = Math.floor(left / 864e5), h = Math.floor((left % 864e5) / 36e5), m = Math.floor((left % 36e5) / 6e4), s = Math.floor((left % 6e4) / 1e3);
  return `${d ? `${d}d ` : ""}${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function Price({ offer, t, light, size = "md", testid = "landing-price", showExpiry = true }) {
  const cd = useCountdown(offer?.promo_active ? offer.promo_expiry : null);
  if (!offer) return <div className="h-8 w-28 rounded-lg bg-current opacity-10 animate-pulse" />;
  const big = size === "lg" ? "text-4xl sm:text-5xl" : size === "sm" ? "text-xl" : "text-3xl";
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap items-baseline gap-x-2.5">
        <span className={`font-display font-extrabold tracking-tight ${big} ${light ? "text-white" : "text-[#102B3F]"}`} data-testid={`${testid}-current`}>{rs(offer.price)}</span>
        {offer.promo_active && <span className={`text-base line-through ${light ? "text-white/45" : "text-[#7D8C96]"}`} data-testid={`${testid}-normal`}>{rs(offer.normal_price)}</span>}
      </div>
      {showExpiry && cd && <div className={`text-[12px] font-semibold mt-0.5 ${light ? "text-[#5FE0CB]" : "text-[#088F7E]"}`} data-testid={`${testid}-countdown`}>{t.price.ends} {cd}</div>}
    </div>
  );
}

export function CtaButton({ onClick, children, testid, dark, className = "" }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`lp-btn ${dark ? "lp-btn-light" : ""} ${className}`}>{children}</button>
  );
}

export const LEAD_DONE = "sb_lead_submitted";
export const inputCls = "w-full h-12 rounded-xl border border-[#D5E3E8] bg-white px-4 text-base focus:outline-none focus:ring-2 focus:ring-[#0BAF9A]";

export function GuideMockup({ t, idea, compact, testid }) {
  return (
    <div className={`lp-mock relative mx-auto ${compact ? "w-[200px] sm:w-[250px]" : "w-[220px] sm:w-[280px] lg:w-[310px]"}`} data-testid={testid}>
      <div className="lp-cover">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#0BAF9A]/25 blur-2xl" />
        <div className="absolute inset-x-0 bottom-0 h-1/3 lp-grid opacity-40" />
        <div className="relative flex items-center gap-2"><img src={LOGO_ICON} alt="" className="h-6 w-6 rounded-md" /><span className="font-display font-bold text-white text-sm tracking-tight">sidebizz</span></div>
        <div className="relative mt-auto">
          <div className="lp-150 font-display font-extrabold leading-none">150</div>
          <div className="font-display font-extrabold text-white text-lg sm:text-xl tracking-wide mt-1">{t.mock.title}</div>
          <div className="h-1 w-12 rounded-full bg-[#0BAF9A] my-3" />
          <div className="text-[10px] sm:text-[11px] font-bold tracking-[0.25em] text-[#5FE0CB]">{t.mock.edition}</div>
        </div>
      </div>
      <div className="lp-float lp-float-a" aria-hidden>
        <div className="text-[9px] font-bold tracking-widest text-[#088F7E] flex items-center gap-1"><ListChecks className="h-3 w-3" />{t.mock.inside}</div>
        {t.inside.items.slice(0, 3).map(([x]) => <div key={x} className="flex items-center gap-1.5 text-[10px] font-semibold text-[#102B3F] mt-1"><Check className="h-3 w-3 text-[#0BAF9A]" />{x}</div>)}
      </div>
      {idea && (
        <div className="lp-float lp-float-b" aria-hidden>
          <div className="text-[9px] text-[#7D8C96] font-semibold">#{idea.number} · {idea.category}</div>
          <div className="font-display font-bold text-[11px] text-[#102B3F] leading-tight">{idea.name}</div>
          <div className="grid grid-cols-2 gap-1 mt-1.5 text-[9px]">
            <div className="rounded-md bg-[#EAF8F4] px-1.5 py-1"><div className="text-[#7D8C96]">{t.mock.startup}</div><b className="text-[#102B3F]">{idea.startup_cost?.replace("Rs ", "")}</b></div>
            <div className="rounded-md bg-[#EAF8F4] px-1.5 py-1"><div className="text-[#7D8C96]">{t.mock.level}</div><b className="text-[#102B3F]">{idea.difficulty}</b></div>
          </div>
        </div>
      )}
      <div className="lp-float lp-float-c" aria-hidden>
        <TrendingUp className="h-3.5 w-3.5 text-[#0BAF9A]" /><span className="text-[10px] font-bold text-[#102B3F]">{t.mock.rev}</span>
      </div>
    </div>
  );
}
